import React from 'react';
import { Link, graphql } from 'gatsby';
import styled from 'styled-components';

import SEO from '../components/Seo';
import Layout from '../components/Layout';
import { ForwardArrow } from '../components/Icons';
import PostPreview from '../components/PostPreview';
import BookPreview from '../components/BookPreview';
import NotePreview from '../components/NotePreview';

import { H3, Paragraph } from '../components/StyledComponents/TextStyles';

import { bpMaxXXS } from '../utils/breakpoints';
import ArtistPreview, { PREVIEW_SIZES } from '../components/ArtistPreview';

const Section = styled.section`
	svg {
		width: 14px;
		display: inline-block;
	}

	${bpMaxXXS} {
		svg {
			width: 11px;
		}
	}
`;

const BriefAboutSection = () => {
	return (
		<Section
			className="p-4 sm:p-6 rounded"
			style={{
				color: 'var(--color-dark-background)',
				backgroundColor: 'var(--color-tertiary)',
				transition: 'var(--default-transition)',
			}}
		>
			<header>
				<H3 customClass="mb-4 sm:mb-6">
					Hi{' '}
					<span
						role="img"
						className="waving-hand"
						aria-label="Waving Hand Emoji"
					>
						👋
					</span>{' '}
					I am Mohit.
				</H3>
			</header>
			<div className="mb-4 sm:mb-6">
				<Paragraph>
					I am a design-centric <strong>Software Engineer.</strong> I mostly
					work on building websites and apps. Apart from that, I watch & play
					football for fun, listen to music all the time, and try to learn stuff
					by reading books and blogs. But mostly, I am fighting procrastination
					and overthinking things.
					<strong> In a love-hate relationship with JavaScript.</strong>
				</Paragraph>
				<Paragraph>
					CunningKruger is my foray into the blogging world.
				</Paragraph>
			</div>
			<strong className="text-xs sm:text-sm text-left">
				<Link
					to="/about/"
					className="inline-block"
					style={{ color: 'inherit' }}
				>
					<span className="inline-block mr-1">More About Me</span>
					<ForwardArrow />
				</Link>
			</strong>
		</Section>
	);
};

const PostList = ({ posts }) => {
	return posts.map(({ node }, index) => {
		const title = node.frontmatter.title || node.fields.slug;
		const isLast = index === posts.length - 1;

		if (process.env.NODE_ENV !== `production` || node.frontmatter.published) {
			return (
				<PostPreview
					key={node.fields.slug}
					isLast={isLast}
					title={title}
					node={node}
				/>
			);
		}
	});
};

const BooksList = ({ books, childItemClass }) => {
	return books.map((book, index) => {
		const isFirst = index === 0;

		return (
			<BookPreview
				key={book.node.frontmatter.title}
				book={book}
				childItemClass={isFirst ? '' : childItemClass}
			/>
		);
	});
};

const NotesList = ({ notes }) => {
	return notes.map((note, index) => {
		const isLastNote = index === notes.length - 1;

		return (
			<NotePreview key={note.node.id} note={note} isLastNote={isLastNote} />
		);
	});
};

const ArtistsList = ({ artists }) => {
	return artists.map((artist, index) => {
		const size =
			index === 0
				? PREVIEW_SIZES.LARGE
				: index <= 2
				? PREVIEW_SIZES.MEDIUM
				: index <= 5
				? PREVIEW_SIZES.SMALL
				: PREVIEW_SIZES.EXTRA_SMALL;

		const isLastArtist = index === artists.length - 1;

		return (
			<ArtistPreview
				key={artist.id}
				size={size}
				isLastArtist={isLastArtist}
				artist={artist}
			/>
		);
	});
};

const BlogIndex = ({ data, location }) => {
	console.log(data);
	const siteTitle = data.site.siteMetadata.title;
	const posts = data.posts.edges;
	const books = data.books.edges;
	const notes = data.notes.edges;
	const artists = data.topArtists.topArtistsData.longTerm.items;

	return (
		<Layout location={location} title={siteTitle}>
			<SEO title="Home" />
			<BriefAboutSection />
			<section className="recent-posts pt-12 sm:pt-16 md:pt-20">
				<header>
					<H3 customClass="mb-4 sm:mb-8">Recent Posts</H3>
				</header>
				<PostList posts={posts} />
			</section>
			<Section className="reading-list pt-12 sm:pt-16 md:pt-20">
				<header>
					<H3 customClass="mb-4 sm:mb-8">Bookshelf</H3>
				</header>
				<div className="md:flex md:justify-between">
					<div className="reading-list-summary w-full sm:w-1/3">
						<Paragraph
							customClass="mb-4 sm:mb-8"
							// style={{ color: 'var(--color-gray-800)' }}
						>
							My digital bookshelf, consisting of the books I've read in the
							past and the ones I'm currently reading.
						</Paragraph>
						<strong className="text-xs sm:text-sm text-left inline-block mb-8 sm:mb-0">
							<Link to="/books/" className="inline-block">
								<span className="inline-block mr-1">Browse Bookshelf</span>
								<ForwardArrow />
							</Link>
						</strong>
					</div>
					<div className="flex sm:mb-0 sm:-mt-12">
						<BooksList books={books} childItemClass="ml-8" />
					</div>
				</div>
			</Section>
			<Section>
				<header>
					<H3 customClass="mb-4 sm:mb-8">Notes</H3>
				</header>
				<div className="notes-description">
					<Paragraph customClass="mb-4 sm:mb-8">
						My personal thoughts on art I like/dislike or came across through
						recommendations from my friends or the internet.
					</Paragraph>
				</div>
				<div className="">
					<NotesList notes={notes} />
				</div>
				<div className="text-right">
					<strong className="text-xs sm:text-sm text-left inline-block mb-4 sm:mb-8">
						<Link to="/notes/" className="inline-block">
							<span className="inline-block mr-1">Explore More</span>
							<ForwardArrow />
						</Link>
					</strong>
				</div>
			</Section>
			<Section>
				<header>
					<H3 customClass="mb-4 sm:mb-8">Top Spotify Artists</H3>
				</header>
				<div className="artists-container">
					<ArtistsList artists={artists} />
				</div>
			</Section>
		</Layout>
	);
};

export default BlogIndex;

export const pageQuery = graphql`
	query {
		site {
			siteMetadata {
				title
			}
		}

		posts: allMdx(
			filter: { frontmatter: { type: { eq: "post" }, published: { eq: true } } }
			sort: { fields: [frontmatter___date], order: DESC }
			limit: 3
		) {
			edges {
				node {
					excerpt
					fields {
						slug
					}
					frontmatter {
						date(formatString: "MMMM DD, YYYY")
						title
						description
						published
					}
				}
			}
		}

		books: allMdx(
			filter: {
				frontmatter: {
					type: { eq: "book" }
					published: { eq: true }
					readingStatus: { eq: "reading" }
				}
			}
			sort: { order: DESC, fields: frontmatter___date }
			limit: 2
		) {
			edges {
				node {
					frontmatter {
						slug
						date(formatString: "MMMM DD, YYYY")
						title
						author
						published
						cover {
							childImageSharp {
								fluid(maxWidth: 200, quality: 100) {
									...GatsbyImageSharpFluid
								}
							}
						}
						type
						readingStatus
						excerpt
					}
				}
			}
		}

		notes: allMdx(
			filter: { frontmatter: { published: { eq: true }, type: { eq: "note" } } }
			sort: { order: DESC, fields: frontmatter___date }
			limit: 2
		) {
			edges {
				node {
					id
					body
					frontmatter {
						description
						title
						date(formatString: "MMMM DD, YYYY")
						noteType
						rating
						slug
						type
						author
						year
						cover {
							childImageSharp {
								fluid(maxWidth: 215, quality: 100) {
									...GatsbyImageSharpFluid
								}
							}
						}
					}
				}
			}
		}

		topArtists {
			topArtistsData {
				longTerm {
					items {
						external_urls {
							spotify
						}
						images {
							height
							url
							width
						}
						id
						name
						popularity
						type
						genres
					}
				}
				mediumTerm {
					items {
						external_urls {
							spotify
						}
						images {
							height
							url
							width
						}
						id
						name
						popularity
						type
						genres
					}
				}
				shortTerm {
					items {
						external_urls {
							spotify
						}
						images {
							height
							url
							width
						}
						id
						name
						popularity
						type
						genres
					}
				}
			}
		}
	}
`;
